import React, { useState } from 'react';
import { FileText, Download } from 'lucide-react';
import { mockData } from '../mock';
import LeadCaptureModal from './LeadCaptureModal';

const iconMap = {
  FileText: FileText,
  Download: Download
};

const ResourcesSection = () => {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedResource, setSelectedResource] = useState(null);

  const handleDownloadClick = (resource) => {
    // Open modal to capture lead information
    setSelectedResource(resource);
    setIsModalOpen(true);
  };

  const handleDownload = (resource) => {
    // Create a temporary anchor element to trigger download
    const link = document.createElement('a');
    link.href = resource.fileUrl;
    link.download = resource.fileName;
    link.target = '_blank';
    link.rel = 'noopener noreferrer';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const handleModalSuccess = () => {
    // Trigger the actual download after successful lead capture
    if (selectedResource) {
      handleDownload(selectedResource);
    }
  };

  return (
    <section className="bg-[#F1EFE8] py-20 lg:py-28">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <div className="text-center mb-16">
          <p className="text-[#5DCAA5] font-semibold text-lg mb-4">Our Resources</p>
          <h2 className="text-3xl lg:text-4xl xl:text-5xl font-bold text-[#2C2C2A] mb-6 tracking-tight">
            Everything You Need to Get Started
          </h2>
        </div>

        {/* Resources Grid */}
        <div className="grid md:grid-cols-2 gap-8 max-w-5xl mx-auto">
          {mockData.resources.map((resource) => {
            const IconComponent = iconMap[resource.icon];
            const bgColor = resource.id === 1 ? '#0F6E56' : '#EF9F27';
            return (
              <div
                key={resource.id}
                className="group relative overflow-hidden rounded-3xl p-8 lg:p-10 text-white transition-all duration-300 hover:shadow-2xl transform hover:-translate-y-2"
                style={{ backgroundColor: bgColor }}
              >
                {/* Decorative background */}
                <div className="absolute top-0 right-0 w-32 h-32 bg-white/5 rounded-full -translate-y-16 translate-x-16"></div>
                <div className="absolute bottom-0 left-0 w-40 h-40 bg-white/5 rounded-full translate-y-20 -translate-x-20"></div>
                
                {/* Content */}
                <div className="relative z-10 space-y-6">
                  <div className="w-16 h-16 bg-white/10 rounded-2xl flex items-center justify-center group-hover:bg-white/20 transition-colors">
                    <IconComponent className="w-8 h-8 text-white" />
                  </div>
                  
                  <div>
                    <h3 className="text-2xl font-bold mb-2">{resource.title}</h3>
                    <p className="text-white/80 text-lg">{resource.description}</p>
                  </div>
                  
                  <button 
                    onClick={() => handleDownloadClick(resource)}
                    className="inline-flex items-center space-x-2 px-6 py-3 bg-white text-[#2C2C2A] rounded-full hover:bg-white/90 transition-colors font-semibold group cursor-pointer"
                  >
                    <span>Download</span>
                    <Download className="w-4 h-4 group-hover:translate-y-1 transition-transform" />
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Lead Capture Modal */}
      <LeadCaptureModal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        resource={selectedResource}
        onSuccess={handleModalSuccess}
      />
    </section>
  );
};

export default ResourcesSection;